import { useNavigate } from "react-router-dom";
import { useState } from "react";
import { Check, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import api, { apiErr } from "../lib/api";

const PLANS = [
  {
    name: "Candidate Free",
    price: "$0",
    period: "forever",
    cta: "Start free",
    pkg: null,
    role: "candidate",
    features: ["AI resume parsing", "Match Score on every job", "Job recommendations", "AI career chatbot"],
    dark: false,
  },
  {
    name: "Candidate Premium",
    price: "$19",
    period: "/month",
    cta: "Go Premium",
    pkg: "candidate_premium",
    role: "candidate",
    features: ["Everything in Free", "Resume optimization", "AI career coach", "Priority visibility", "Salary insights"],
    dark: false,
    badge: "Popular",
  },
  {
    name: "Employer Recruiter Pro",
    price: "$299",
    period: "/month",
    cta: "Hire smarter",
    pkg: "employer_pro",
    role: "employer",
    features: ["Unlimited AI matching", "Candidate shortlists", "AI Recruiter Copilot", "AI screening", "Interview automation", "Includes Flex + Remote search"],
    dark: true,
  },
  {
    name: "Flex Marketplace",
    price: "$99",
    period: "/month",
    cta: "Search flex talent",
    pkg: "flex_pro",
    role: "employer",
    features: ["Search Darriker Flex (on-site)", "Search Darriker Remote", "Availability & wage filters", "AI match scores", "Direct click-to-call contact"],
    dark: false,
    badge: "Marketplace",
  },
];

export default function Pricing() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [loading, setLoading] = useState(null);

  const subscribe = async (plan) => {
    if (!plan.pkg) {
      nav(`/auth?mode=register&role=${plan.role}`);
      return;
    }
    if (!user) {
      nav(`/auth?mode=register&role=${plan.role}`);
      return;
    }
    setLoading(plan.pkg);
    try {
      const r = await api.post("/payments/checkout", {
        package_id: plan.pkg,
        origin_url: window.location.origin,
      });
      window.location.href = r.data.url;
    } catch (e) {
      toast.error(apiErr(e));
      setLoading(null);
    }
  };

  return (
    <main data-testid="pricing-page" className="max-w-7xl mx-auto px-5 py-20">
      <div className="max-w-2xl">
        <div className="font-mono2 text-xs uppercase text-[#002FA7] mb-3">Pricing</div>
        <h1 className="font-display font-black text-5xl tracking-tighter">Plans that pay for themselves on the first great hire.</h1>
      </div>
      <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-5 mt-14">
        {PLANS.map((p) => {
          const current = user && p.pkg && user.plan === p.pkg;
          return (
            <div
              key={p.name}
              data-testid={`plan-${p.name.toLowerCase().replace(/\s+/g, "-")}`}
              className={`border p-8 flex flex-col ${p.dark ? "bg-[#0A0A0A] text-white border-[#0A0A0A]" : "border-[#0A0A0A] bg-white"}`}
            >
              <div className="flex items-center justify-between">
                <div className="font-display font-bold text-lg">{p.name}</div>
                {p.badge && <span className="bg-[#002FA7] text-white text-xs font-mono2 px-2 py-0.5">{p.badge}</span>}
              </div>
              <div className="mt-5 flex items-end gap-1">
                <span className="font-mono2 font-bold text-5xl">{p.price}</span>
                <span className={`mb-1.5 text-sm ${p.dark ? "text-[#9CA3AF]" : "text-[#4B5563]"}`}>{p.period}</span>
              </div>
              <ul className="mt-7 space-y-2.5 flex-1">
                {p.features.map((f) => (
                  <li key={f} className="flex gap-2 text-sm">
                    <Check size={16} className="text-[#002FA7] shrink-0 mt-0.5" /> {f}
                  </li>
                ))}
              </ul>
              {current ? (
                <div data-testid={`plan-current-${p.pkg}`} className="mt-8 text-center py-3 font-medium border border-[#10B981] text-[#10B981]">
                  ✓ Current plan
                </div>
              ) : (
                <button
                  data-testid={`subscribe-${p.pkg || "free"}`}
                  onClick={() => subscribe(p)}
                  disabled={loading === (p.pkg || "free")}
                  className={`mt-8 text-center py-3 font-medium inline-flex items-center justify-center gap-2 disabled:opacity-60 ${p.dark ? "bg-[#002FA7] hover:bg-white hover:text-[#0A0A0A] transition-colors" : "btn-ink"}`}
                >
                  {loading === (p.pkg || "free") && <Loader2 size={16} className="animate-spin" />}
                  {loading === (p.pkg || "free") ? "Redirecting…" : p.cta}
                </button>
              )}
            </div>
          );
        })}
      </div>
      <p className="mt-8 font-mono2 text-xs text-[#9CA3AF]">Secure checkout via Stripe. Test mode — use card 4242 4242 4242 4242, any future expiry & CVC.</p>
    </main>
  );
}
